import { DragEvent, FormEvent, PointerEvent, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../context/AuthProvider";
import {
  createTodo,
  updateTodo,
  deleteTodo,
  getTodos,
  createSchedule,
  updateSchedule,
  deleteSchedule,
  reviewCard,
  moveTodo,
  TodoDashboard,
  Todo,
  TodoCategory,
  TodoInput,
  Frequency,
  ScheduleInput,
} from "../../api/todos";
import { ErrorNotice } from "../tooling/ToolingShared";
import "./todos.css";

const categories: {
  value: TodoCategory;
  label: string;
  description: string;
}[] = [
  { value: "system", label: "System", description: "System related work" },
  {
    value: "blocker",
    label: "Blocker",
    description: "Clear what is holding things up",
  },
  {
    value: "short_term",
    label: "Not in 3-4 days buffer",
    description: "Work to finish soon",
  },
];
const dateLabel = (value: string) =>
  new Date(`${value}T12:00:00`).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
const localToday = () =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
const daysAhead = (today: string, days: number) => {
  const date = new Date(`${today}T12:00:00`);
  date.setDate(date.getDate() + days);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(date.getDate()).padStart(2, "0")}`;
};
const defaultDue = (today: string, category: TodoCategory) =>
  daysAhead(today, category === "short_term" ? 4 : 0);

function CategoryButtons({
  value,
  onChange,
}: {
  value: TodoCategory;
  onChange: (value: TodoCategory) => void;
}) {
  return (
    <div className="todos-type-buttons" role="group" aria-label="Task type">
      {categories.map((category) => (
        <button
          key={category.value}
          type="button"
          className={`todos-type-button todos-type-button--${category.value}`}
          aria-pressed={value === category.value}
          onClick={() => onChange(category.value)}
        >
          {category.label}
        </button>
      ))}
    </div>
  );
}

export default function Todos() {
  const { user } = useAuth();
  const client = useQueryClient();
  const key = ["todos", user?.id];
  const dashboard = useQuery({
    queryKey: key,
    queryFn: getTodos,
    refetchInterval: 60000,
  });
  const today = dashboard.data?.today || localToday();
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState<TodoCategory>("system");
  const [due, setDue] = useState("");
  const [search, setSearch] = useState("");
  const [showCompleted, setShowCompleted] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [editing, setEditing] = useState<Todo>();
  const [dragging, setDragging] = useState<number | null>(null);
  const pointerDrag = useRef<{
    id: number;
    target: { category: TodoCategory; beforeId: number | null } | null;
  } | null>(null);
  const [dropTarget, setDropTarget] = useState<{
    category: TodoCategory;
    beforeId: number | null;
  } | null>(null);
  const [scheduleTitle, setScheduleTitle] = useState("");
  const [scheduleCategory, setScheduleCategory] =
    useState<TodoCategory>("system");
  const [frequency, setFrequency] = useState<Frequency>("daily");
  const [startDate, setStartDate] = useState("");
  const refresh = () => client.invalidateQueries({ queryKey: key });
  const add = useMutation({
    mutationFn: createTodo,
    onSuccess: () => {
      setTitle("");
      setDue("");
      refresh();
    },
  });
  const update = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: Partial<TodoInput> & { completed?: boolean };
    }) => updateTodo(id, data),
    onSuccess: refresh,
  });
  const remove = useMutation({ mutationFn: deleteTodo, onSuccess: refresh });
  const saveEdit = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: TodoInput & { notes: string };
    }) => updateTodo(id, data),
    onSuccess: () => {
      setEditing(undefined);
      refresh();
    },
  });
  const addRecurring = useMutation({
    mutationFn: (data: ScheduleInput) => createSchedule(data),
    onSuccess: () => {
      setScheduleTitle("");
      refresh();
    },
  });
  const toggleSchedule = useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
      updateSchedule(id, { enabled }),
    onSuccess: refresh,
  });
  const removeSchedule = useMutation({
    mutationFn: deleteSchedule,
    onSuccess: refresh,
  });
  const review = useMutation({ mutationFn: reviewCard, onSuccess: refresh });
  const move = useMutation({
    mutationFn: moveTodo,
    onMutate: async ({ id, category, before_id }) => {
      await client.cancelQueries({ queryKey: key });
      const previous = client.getQueryData<TodoDashboard>(key);
      if (previous) {
        const task = previous.todos.find((item) => item.id === id);
        if (task) {
          const remaining = previous.todos.filter((item) => item.id !== id);
          const target = remaining.filter((item) => item.category === category);
          const index =
            before_id === null
              ? target.length
              : target.findIndex((item) => item.id === before_id);
          target.splice(index < 0 ? target.length : index, 0, {
            ...task,
            category,
          });
          client.setQueryData<TodoDashboard>(key, {
            ...previous,
            todos: [
              ...remaining.filter((item) => item.category !== category),
              ...target.map((item, position) => ({ ...item, position })),
            ],
          });
        }
      }
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) client.setQueryData(key, context.previous);
    },
    onSettled: refresh,
  });
  const todos = dashboard.data?.todos || [];
  const approvals = dashboard.data?.approvals || [];
  const matches = (text: string) =>
    text.toLowerCase().includes(search.toLowerCase().trim());
  const visible = todos.filter(
    (todo) => (showCompleted || !todo.completed) && matches(todo.title)
  );
  const visibleApprovals = approvals.filter(
    (card) =>
      (showCompleted || !card.reviewed) &&
      matches(
        `${card.tool.code} ${card.detail} ${card.tool.parts
          .map((part) => part.part_name)
          .join(" ")}`
      )
  );
  const canReview = ["admin", "superadmin"].includes(user?.role || "");
  const destination = (event: DragEvent<HTMLElement>, task: Todo) => {
    const after =
      event.clientY >
      event.currentTarget.getBoundingClientRect().top +
        event.currentTarget.getBoundingClientRect().height / 2;
    const target = todos.filter(
      (item) => item.category === task.category && item.id !== dragging
    );
    const index = target.findIndex((item) => item.id === task.id);
    return {
      category: task.category,
      beforeId: after ? target[index + 1]?.id ?? null : task.id,
    };
  };
  const drop = (
    event: DragEvent<HTMLElement>,
    target: { category: TodoCategory; beforeId: number | null }
  ) => {
    event.preventDefault();
    event.stopPropagation();
    const id = dragging;
    setDragging(null);
    setDropTarget(null);
    if (id !== null && id !== target.beforeId && !move.isLoading)
      move.mutate({
        id,
        category: target.category,
        before_id: target.beforeId,
      });
  };
  const movePointer = (event: PointerEvent<HTMLButtonElement>) => {
    if (!pointerDrag.current) return;
    const element = document.elementFromPoint(event.clientX, event.clientY);
    const column = element?.closest<HTMLElement>("[data-todo-category]");
    if (!column) {
      pointerDrag.current.target = null;
      setDropTarget(null);
      return;
    }
    const category = column.dataset.todoCategory as TodoCategory;
    const row = element?.closest<HTMLElement>("[data-todo-id]");
    let beforeId: number | null = null;
    if (row) {
      const id = Number(row.dataset.todoId);
      if (id === pointerDrag.current.id) beforeId = id;
      else {
        const rect = row.getBoundingClientRect();
        const target = todos.filter(
          (item) =>
            item.category === category && item.id !== pointerDrag.current?.id
        );
        const index = target.findIndex((item) => item.id === id);
        beforeId =
          event.clientY > rect.top + rect.height / 2
            ? target[index + 1]?.id ?? null
            : id;
      }
    }
    pointerDrag.current.target = { category, beforeId };
    setDropTarget(pointerDrag.current.target);
    const scroller = column.closest<HTMLElement>(".home-container-inside");
    if (scroller) {
      const rect = scroller.getBoundingClientRect();
      if (event.clientY > rect.bottom - 40) scroller.scrollBy(0, 16);
      else if (event.clientY < rect.top + 40) scroller.scrollBy(0, -16);
    }
  };
  const endPointer = () => {
    const current = pointerDrag.current;
    pointerDrag.current = null;
    setDragging(null);
    setDropTarget(null);
    if (
      current?.target &&
      current.id !== current.target.beforeId &&
      !move.isLoading
    ) {
      move.mutate({
        id: current.id,
        category: current.target.category,
        before_id: current.target.beforeId,
      });
    }
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    add.mutate({
      title,
      category,
      due_date: due || defaultDue(today, category),
    });
  };

  return (
    <section className="todos-page" aria-labelledby="todos-title">
      <header className="todos-header">
        <div>
          <span className="todos-eyebrow">YOUR DAILY WORKSPACE</span>
          <h2 id="todos-title">My TODOs</h2>
          <p>Drag tasks to reorder them or move them to another type.</p>
        </div>
        <button
          className="todos-secondary"
          onClick={() => setSettingsOpen(true)}
        >
          <i className="bi bi-arrow-repeat" aria-hidden="true" /> Recurring
          settings
        </button>
      </header>
      <ErrorNotice error={dashboard.error} retry={() => dashboard.refetch()} />
      <ErrorNotice
        error={
          add.error ||
          update.error ||
          remove.error ||
          review.error ||
          move.error
        }
      />
      <form className="todos-quick-add" onSubmit={submit}>
        <input
          aria-label="New task"
          placeholder="What needs to get done?"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={300}
          required
          autoFocus
        />
        <CategoryButtons
          value={category}
          onChange={(value) => {
            setCategory(value);
            setDue("");
          }}
        />
        <input
          type="date"
          aria-label="Due date"
          value={due || defaultDue(today, category)}
          onChange={(e) => setDue(e.target.value)}
          required
        />
        <button
          className="todos-primary"
          disabled={add.isLoading || !title.trim()}
        >
          {add.isLoading ? "Adding…" : "+ Add task"}
        </button>
      </form>
      <div className="todos-controls">
        <span>
          {todos.filter((task) => !task.completed).length +
            (canReview
              ? approvals.filter((card) => !card.reviewed).length
              : 0)}{" "}
          open tasks ·{" "}
          {
            todos.filter((task) => !task.completed && task.due_date < today)
              .length
          }{" "}
          overdue
        </span>
        <input
          type="search"
          aria-label="Search tasks"
          placeholder="Search tasks…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label>
          <input
            type="checkbox"
            checked={showCompleted}
            onChange={(e) => setShowCompleted(e.target.checked)}
          />{" "}
          Show completed / reviewed
        </label>
      </div>
      {dashboard.isLoading ? (
        <p role="status">Loading your tasks…</p>
      ) : (
        dashboard.data && (
          <>
            <div className="todos-columns">
              {categories.map((group) => (
                <section
                  className={`todos-group todos-group--${group.value} ${
                    dropTarget?.category === group.value ? "is-drop-target" : ""
                  }`}
                  key={group.value}
                  aria-label={group.label}
                  data-todo-category={group.value}
                  onDragOver={(event) => {
                    if (dragging === null || move.isLoading) return;
                    event.preventDefault();
                    event.dataTransfer.dropEffect = "move";
                    setDropTarget({ category: group.value, beforeId: null });
                  }}
                  onDrop={(event) =>
                    drop(event, { category: group.value, beforeId: null })
                  }
                >
                  <header>
                    <h3>
                      {group.label}
                      <span>
                        {todos.filter(
                          (task) =>
                            task.category === group.value && !task.completed
                        ).length +
                          (group.value === "blocker" && canReview
                            ? approvals.filter((card) => !card.reviewed).length
                            : 0)}
                      </span>
                    </h3>
                    <p>{group.description}</p>
                  </header>
                  {visible
                    .filter((task) => task.category === group.value)
                    .map((task) => (
                      <article
                        className={`todos-item ${
                          task.completed ? "is-complete" : ""
                        } ${dragging === task.id ? "is-dragging" : ""} ${
                          dropTarget?.beforeId === task.id
                            ? "is-drop-before"
                            : ""
                        }`}
                        key={task.id}
                        data-todo-id={task.id}
                        draggable={!move.isLoading}
                        onDragStart={(event) => {
                          event.dataTransfer.setData(
                            "application/x-pressco-todo",
                            String(task.id)
                          );
                          event.dataTransfer.effectAllowed = "move";
                          setDragging(task.id);
                        }}
                        onDragEnd={() => {
                          setDragging(null);
                          setDropTarget(null);
                        }}
                        onDragOver={(event) => {
                          if (
                            dragging === null ||
                            move.isLoading ||
                            dragging === task.id
                          )
                            return;
                          event.preventDefault();
                          event.stopPropagation();
                          event.dataTransfer.dropEffect = "move";
                          setDropTarget(destination(event, task));
                        }}
                        onDrop={(event) =>
                          drop(
                            event,
                            dragging === task.id
                              ? { category: task.category, beforeId: task.id }
                              : destination(event, task)
                          )
                        }
                      >
                        <button
                          type="button"
                          className="todos-drag-handle"
                          title="Drag to reorder or move to another type"
                          aria-label={`Move ${task.title}. Drag or use up and down arrow keys.`}
                          disabled={move.isLoading}
                          onPointerDown={(event) => {
                            if (event.button !== 0 || move.isLoading) return;
                            event.preventDefault();
                            event.stopPropagation();
                            event.currentTarget.focus();
                            event.currentTarget.setPointerCapture(
                              event.pointerId
                            );
                            pointerDrag.current = { id: task.id, target: null };
                            setDragging(task.id);
                          }}
                          onPointerMove={movePointer}
                          onPointerUp={endPointer}
                          onPointerCancel={() => {
                            pointerDrag.current = null;
                            setDragging(null);
                            setDropTarget(null);
                          }}
                          onKeyDown={(event) => {
                            if (
                              move.isLoading ||
                              !["ArrowUp", "ArrowDown"].includes(event.key)
                            )
                              return;
                            event.preventDefault();
                            const items = visible.filter(
                              (item) => item.category === task.category
                            );
                            const index = items.findIndex(
                              (item) => item.id === task.id
                            );
                            if (event.key === "ArrowUp" && index > 0)
                              move.mutate({
                                id: task.id,
                                category: task.category,
                                before_id: items[index - 1].id,
                              });
                            if (
                              event.key === "ArrowDown" &&
                              index < items.length - 1
                            )
                              move.mutate({
                                id: task.id,
                                category: task.category,
                                before_id: items[index + 2]?.id ?? null,
                              });
                          }}
                        >
                          <i
                            className="bi bi-grip-vertical"
                            aria-hidden="true"
                          />
                        </button>
                        <input
                          className="todos-check"
                          type="checkbox"
                          aria-label={`Complete ${task.title}`}
                          checked={task.completed}
                          disabled={update.isLoading}
                          onChange={() =>
                            update.mutate({
                              id: task.id,
                              data: { completed: !task.completed },
                            })
                          }
                        />
                        <div className="todos-item-content">
                          <button
                            className="todos-task-title"
                            onClick={() => {
                              saveEdit.reset();
                              setEditing(task);
                            }}
                          >
                            {task.title}
                          </button>
                          <div className="todos-item-meta">
                            <span
                              className={
                                !task.completed &&
                                task.due_date <= daysAhead(today, 2)
                                  ? "todos-overdue"
                                  : ""
                              }
                            >
                              {task.completed
                                ? "Completed"
                                : task.due_date < today
                                ? "Overdue · " + dateLabel(task.due_date)
                                : task.due_date === today
                                ? "Due today"
                                : "Due " + dateLabel(task.due_date)}
                            </span>
                            {task.schedule_id && (
                              <span>
                                <i
                                  className="bi bi-arrow-repeat"
                                  aria-hidden="true"
                                />{" "}
                                Recurring
                              </span>
                            )}
                          </div>
                        </div>
                        {!task.schedule_id && (
                          <button
                            className="todos-icon-button"
                            aria-label={`Delete ${task.title}`}
                            disabled={remove.isLoading}
                            onClick={() => {
                              if (window.confirm(`Delete “${task.title}”?`))
                                remove.mutate(task.id);
                            }}
                          >
                            <i className="bi bi-trash" aria-hidden="true" />
                          </button>
                        )}
                      </article>
                    ))}
                  {group.value === "blocker" &&
                    canReview &&
                    visibleApprovals.map((card) => (
                      <article
                        className={`todos-item todos-tooling-item ${
                          card.reviewed ? "is-complete" : ""
                        }`}
                        key={`tooling-${card.id}`}
                      >
                        <input
                          className="todos-check"
                          type="checkbox"
                          aria-label={`Mark card ${card.id} reviewed`}
                          title="Mark reviewed for you. Approve this card in Tool Development."
                          checked={card.reviewed}
                          disabled={review.isLoading}
                          onChange={() => review.mutate(card)}
                        />
                        <div className="todos-item-content">
                          <div className="todos-task-heading">
                            <Link
                              to={`/tool-development?card=${card.id}`}
                              className="todos-task-title"
                            >
                              {card.tool.code} ·{" "}
                              {card.tool.parts[0]?.part_name ||
                                "No linked part"}{" "}
                              · Card #{card.id}
                            </Link>
                            <span className="todos-tooling-tag">
                              Tool Development
                            </span>
                          </div>
                          <p>{card.detail}</p>
                          <div className="todos-item-meta">
                            <span>
                              {card.deletion_pending
                                ? "Deletion approval pending"
                                : "Approval pending"}
                            </span>
                            {card.reviewed && <span>Reviewed by you</span>}
                          </div>
                        </div>
                      </article>
                    ))}
                  {!visible.some((task) => task.category === group.value) &&
                    !(
                      group.value === "blocker" &&
                      canReview &&
                      visibleApprovals.length > 0
                    ) && (
                      <p className="todos-empty">
                        {search
                          ? "No matching tasks."
                          : "All clear. Add a task above."}
                      </p>
                    )}
                </section>
              ))}
            </div>
          </>
        )
      )}
      <Modal
        show={settingsOpen}
        onHide={() => setSettingsOpen(false)}
        size="lg"
        scrollable
        aria-labelledby="recurring-title"
      >
        <Modal.Header closeButton>
          <Modal.Title id="recurring-title">
            Recurring TODO settings
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="todos-settings">
          <p>
            A fresh task appears on each scheduled date in India time.
            Unfinished tasks stay on your list. Monthly and yearly dates adjust
            to the last day of shorter months.
          </p>
          <ErrorNotice
            error={
              addRecurring.error || toggleSchedule.error || removeSchedule.error
            }
          />
          <form
            onSubmit={(event) => {
              event.preventDefault();
              addRecurring.mutate({
                title: scheduleTitle,
                category: scheduleCategory,
                frequency,
                start_date: startDate || today,
              });
            }}
          >
            <label>
              Task
              <input
                value={scheduleTitle}
                onChange={(e) => setScheduleTitle(e.target.value)}
                required
                maxLength={300}
                placeholder="e.g. Check pending dispatches"
              />
            </label>
            <div className="todos-settings-fields">
              <div className="todos-field">
                <span>Type</span>
                <CategoryButtons
                  value={scheduleCategory}
                  onChange={setScheduleCategory}
                />
              </div>
              <label>
                Repeat
                <select
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value as Frequency)}
                >
                  <option value="daily">Daily</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </label>
              <label>
                First date
                <input
                  type="date"
                  value={startDate || today}
                  min={today}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                />
              </label>
            </div>
            <button
              className="todos-primary"
              disabled={addRecurring.isLoading || !scheduleTitle.trim()}
            >
              {addRecurring.isLoading ? "Saving…" : "+ Add recurring task"}
            </button>
          </form>
          <h4 className="mt-4">Your recurring tasks</h4>
          {(dashboard.data?.schedules || []).map((schedule) => (
            <div className="todos-schedule" key={schedule.id}>
              <div>
                <strong>{schedule.title}</strong>
                <p>
                  {
                    categories.find(
                      (group) => group.value === schedule.category
                    )?.label
                  }{" "}
                  · {schedule.frequency} ·{" "}
                  {schedule.enabled
                    ? "Next: " + dateLabel(schedule.next_due)
                    : "Paused"}
                </p>
              </div>
              <button
                className="todos-secondary"
                disabled={toggleSchedule.isLoading}
                onClick={() =>
                  toggleSchedule.mutate({
                    id: schedule.id,
                    enabled: !schedule.enabled,
                  })
                }
              >
                {schedule.enabled ? "Pause" : "Resume"}
              </button>
              <button
                className="todos-icon-button"
                aria-label={`Delete recurring schedule ${schedule.title}`}
                disabled={removeSchedule.isLoading}
                onClick={() => {
                  if (
                    window.confirm(
                      "Remove this recurring schedule? Existing tasks will stay on your list."
                    )
                  )
                    removeSchedule.mutate(schedule.id);
                }}
              >
                <i className="bi bi-trash" aria-hidden="true" />
              </button>
            </div>
          ))}
          {!dashboard.data?.schedules.length && (
            <p className="todos-empty">No recurring tasks yet.</p>
          )}
        </Modal.Body>
      </Modal>
      <Modal
        show={!!editing}
        onHide={() => setEditing(undefined)}
        aria-labelledby="edit-todo-title"
      >
        <Modal.Header closeButton>
          <Modal.Title id="edit-todo-title">Edit task</Modal.Title>
        </Modal.Header>
        {editing && (
          <Modal.Body className="todos-settings">
            <ErrorNotice error={saveEdit.error} />
            <form
              onSubmit={(event) => {
                event.preventDefault();
                saveEdit.mutate({
                  id: editing.id,
                  data: {
                    title: editing.title,
                    category: editing.category,
                    due_date: editing.due_date,
                    notes: editing.notes,
                  },
                });
              }}
            >
              <label>
                Task
                <input
                  value={editing.title}
                  maxLength={300}
                  required
                  onChange={(e) =>
                    setEditing({ ...editing, title: e.target.value })
                  }
                />
              </label>
              <div className="todos-field">
                <span>Type</span>
                <CategoryButtons
                  value={editing.category}
                  onChange={(value) =>
                    setEditing({ ...editing, category: value })
                  }
                />
              </div>
              <label>
                Due date
                <input
                  type="date"
                  required
                  value={editing.due_date}
                  onChange={(e) =>
                    setEditing({ ...editing, due_date: e.target.value })
                  }
                />
              </label>
              <label>
                Notes
                <textarea
                  value={editing.notes || ""}
                  maxLength={10000}
                  rows={5}
                  placeholder="Add details, reminders or context…"
                  onChange={(e) =>
                    setEditing({ ...editing, notes: e.target.value })
                  }
                />
              </label>
              {editing.schedule_id && (
                <small>Changes apply to this occurrence.</small>
              )}
              <button
                className="todos-primary"
                disabled={saveEdit.isLoading || !editing.title.trim()}
              >
                Save changes
              </button>
            </form>
          </Modal.Body>
        )}
      </Modal>
    </section>
  );
}
